import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, Home } from "lucide-react";
import { Seo } from "../components/Seo";
import { api } from "../lib/api";
import { EVENT } from "../lib/site";

export default function DonationSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const [amount, setAmount] = useState(null);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let attempts = 0;
    let timer;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/donations/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setAmount(data.amount);
          setState("paid");
          return;
        }
        if (["expired", "failed"].includes(data.payment_status)) {
          setState("error");
          return;
        }
      } catch {
        setState("error");
        return;
      }
      if (attempts >= 6) { setState("pending"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <>
      <Seo title="Donation" path="/donation/success" />
      <section className="min-h-[80vh] grid place-items-center px-5 pt-24 pb-16">
        <div className="max-w-lg w-full rounded-3xl bg-white border border-black/5 shadow-warm-lg p-10 sm:p-12 text-center" data-testid="donation-result">
          {state === "checking" && (
            <>
              <Loader2 size={44} className="mx-auto text-terracotta animate-spin" />
              <h1 className="mt-6 font-display text-3xl font-semibold">Confirming your donation…</h1>
              <p className="mt-2 text-ink-soft">Just a moment while we verify your payment.</p>
            </>
          )}
          {state === "paid" && (
            <>
              <CheckCircle2 size={52} className="mx-auto text-forest" />
              <h1 className="mt-6 font-display text-4xl font-semibold">Thank you! 🍁</h1>
              <p className="mt-3 text-ink-soft leading-relaxed">
                Your {amount ? `$${Number(amount).toFixed(2)} CAD ` : ""}donation to {EVENT.charity} was successful.
                Your generosity helps build a more inclusive West Niagara.
              </p>
              <Link to="/" className="mt-8 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-terracotta text-white text-sm font-semibold hover:bg-sienna transition-colors duration-200">
                <Home size={16} /> Back to home
              </Link>
            </>
          )}
          {state === "pending" && (
            <>
              <Loader2 size={44} className="mx-auto text-ochre" />
              <h1 className="mt-6 font-display text-3xl font-semibold">Payment processing</h1>
              <p className="mt-2 text-ink-soft">Your payment is still being processed. If it was completed, you'll receive a Stripe receipt by email shortly.</p>
              <Link to="/" className="mt-8 inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-ink/20 text-sm font-semibold hover:border-terracotta hover:text-terracotta transition-colors duration-200">
                Back to home
              </Link>
            </>
          )}
          {state === "error" && (
            <>
              <XCircle size={48} className="mx-auto text-sienna" />
              <h1 className="mt-6 font-display text-3xl font-semibold">We couldn't confirm this</h1>
              <p className="mt-2 text-ink-soft">Something went wrong verifying your donation. If you were charged, you'll receive a receipt by email.</p>
              <Link to="/sponsor-donation" className="mt-8 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-terracotta text-white text-sm font-semibold hover:bg-sienna transition-colors duration-200">
                Try again
              </Link>
            </>
          )}
        </div>
      </section>
    </>
  );
}
